class OffstripeLoader {
  constructor() {
    this.loader = document.querySelector(
      '[data-offstripe-loader]'
    );

    if (!this.loader) return;

    this.progressFill = this.loader.querySelector(
      '[data-offstripe-loader-progress-fill]'
    );

    this.progress = 0;
    this.progressFrame = null;

    this.startTime = performance.now();

    this.hasFinished = false;
    this.hasSignaledPageReady = false;

    this.fallback = null;
    this.exitFallback = null;

    this.init();
  }


  init() {
    /*
     * Emergency safety fallback.
     *
     * A broken third-party request, video,
     * browser bug, etc. must never leave a
     * visitor permanently behind the loader.
     */
    this.fallback = window.setTimeout(
      () => this.complete(),
      12000
    );


    /*
     * Begin the visual progress immediately.
     */
    this.startProgress();


    /*
     * Wait until the DOM exists so we can
     * automatically identify the first-screen
     * media rather than relying on manually
     * configured Shopify section settings.
     */
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => this.preparePage(),
        { once: true }
      );

      return;
    }

    this.preparePage();
  }


  async preparePage() {
    /*
     * Promote the first meaningful image in the
     * main page content automatically.
     *
     * This replaces the old manual
     * "Priority loading" dependency.
     */
    this.promoteFirstMedia();


    const waits = [
      this.waitForWindowLoad(),
      this.waitForFonts(),
      this.waitForFirstScreenMedia()
    ];


    /*
     * Promise.allSettled means one broken media
     * element cannot prevent the loader from
     * eventually completing.
     */
    await Promise.allSettled(waits);


    this.complete();
  }


  promoteFirstMedia() {
    const main = document.querySelector(
      '#MainContent'
    );

    if (!main) return;


    const mediaElements = Array.from(
      main.querySelectorAll('img, video')
    );


    const firstVisibleMedia =
      mediaElements.find((media) => {
        const rect =
          media.getBoundingClientRect();

        return (
          rect.bottom > 0 &&
          rect.top < window.innerHeight * 1.25
        );
      });


    if (!firstVisibleMedia) return;


    if (firstVisibleMedia.tagName === 'IMG') {
      firstVisibleMedia.loading = 'eager';

      try {
        firstVisibleMedia.fetchPriority = 'high';
      } catch {
        firstVisibleMedia.setAttribute(
          'fetchpriority',
          'high'
        );
      }

      return;
    }


    if (firstVisibleMedia.tagName === 'VIDEO') {
      firstVisibleMedia.preload = 'auto';
    }
  }


  waitForWindowLoad() {
    return new Promise((resolve) => {
      if (document.readyState === 'complete') {
        resolve();
        return;
      }

      window.addEventListener(
        'load',
        resolve,
        { once: true }
      );
    });
  }


  waitForFonts() {
    if (
      !document.fonts ||
      !document.fonts.ready
    ) {
      return Promise.resolve();
    }

    return document.fonts.ready;
  }


  waitForFirstScreenMedia() {
    const main = document.querySelector(
      '#MainContent'
    );

    if (!main) {
      return Promise.resolve();
    }


    const mediaElements = Array.from(
      main.querySelectorAll('img, video')
    );


    const visibleMedia =
      mediaElements.filter((media) => {
        const rect =
          media.getBoundingClientRect();

        return (
          rect.bottom > 0 &&
          rect.top < window.innerHeight * 1.25
        );
      });


    if (!visibleMedia.length) {
      return Promise.resolve();
    }


    return Promise.allSettled(
      visibleMedia.map(
        (media) => this.waitForMedia(media)
      )
    );
  }


  waitForMedia(media) {
    if (media.tagName === 'IMG') {
      return this.waitForImage(media);
    }

    if (media.tagName === 'VIDEO') {
      return this.waitForVideo(media);
    }

    return Promise.resolve();
  }


  waitForImage(image) {
    return new Promise((resolve) => {
      if (image.complete) {
        this.decodeImage(
          image,
          resolve
        );

        return;
      }


      image.addEventListener(
        'load',
        () => {
          this.decodeImage(
            image,
            resolve
          );
        },
        { once: true }
      );


      image.addEventListener(
        'error',
        resolve,
        { once: true }
      );
    });
  }


  async decodeImage(image, resolve) {
    if (
      typeof image.decode === 'function'
    ) {
      try {
        await image.decode();
      } catch {
        /*
         * Decode can reject even when the image
         * is usable. Continue normally.
         */
      }
    }

    resolve();
  }


  waitForVideo(video) {
    return new Promise((resolve) => {
      if (video.readyState >= 2) {
        resolve();
        return;
      }


      video.addEventListener(
        'loadeddata',
        resolve,
        { once: true }
      );


      video.addEventListener(
        'error',
        resolve,
        { once: true }
      );
    });
  }


  startProgress() {
    const update = (time) => {
      if (this.hasFinished) {
        return;
      }


      const elapsed =
        time - this.startTime;


      /*
       * Natural asymptotic loading movement.
       *
       * It moves quickly at first and gradually
       * slows as it approaches 90%.
       *
       * 100% is reserved for real completion.
       */
      const simulatedProgress =
        0.90 *
        (
          1 -
          Math.exp(
            -elapsed / 1800
          )
        );


      this.progress =
        Math.max(
          this.progress,
          simulatedProgress
        );


      this.renderProgress(
        Math.min(
          this.progress,
          0.90
        )
      );


      this.progressFrame =
        requestAnimationFrame(
          update
        );
    };


    this.progressFrame =
      requestAnimationFrame(
        update
      );
  }


  renderProgress(value) {
    if (!this.progressFill) return;


    this.progressFill.style.transform =
      `scaleX(${value})`;
  }


  complete() {
    if (this.hasFinished) return;


    this.hasFinished = true;


    window.clearTimeout(
      this.fallback
    );


    if (this.progressFrame) {
      cancelAnimationFrame(
        this.progressFrame
      );
    }


    /*
     * Let CSS handle the final movement
     * from the current value to 100%.
     */
    this.loader.classList.add(
      'offstripe-loader--completing'
    );


    this.renderProgress(1);


    window.setTimeout(
      () => this.hide(),
      450
    );
  }


  hide() {
    if (!this.loader) return;


    /*
     * Begin the existing loader fade.
     */
    this.loader.classList.add(
      'offstripe-loader--hidden'
    );


    document.documentElement.classList.remove(
      'offstripe-is-loading'
    );


    /*
     * The OFFSTRIPE page-ready signal is sent
     * only after the loader has visually finished
     * leaving the screen.
     *
     * This gives sections one global moment from
     * which entrance animations can begin.
     */
    const finishExit = () => {
      if (!this.loader) return;


      window.clearTimeout(
        this.exitFallback
      );


      this.signalPageReady();


      this.loader.remove();

      this.loader = null;
    };


    const handleTransitionEnd = (event) => {
      if (
        event.target !== this.loader ||
        event.propertyName !== 'opacity'
      ) {
        return;
      }


      this.loader.removeEventListener(
        'transitionend',
        handleTransitionEnd
      );


      finishExit();
    };


    this.loader.addEventListener(
      'transitionend',
      handleTransitionEnd
    );


    /*
     * Safety fallback for browsers or situations
     * where transitionend does not fire.
     *
     * Current OFFSTRIPE loader opacity transition
     * is 600ms.
     */
    this.exitFallback = window.setTimeout(
      () => {
        if (!this.loader) return;


        this.loader.removeEventListener(
          'transitionend',
          handleTransitionEnd
        );


        finishExit();
      },
      700
    );
  }


  signalPageReady() {
    if (this.hasSignaledPageReady) {
      return;
    }


    this.hasSignaledPageReady = true;


    /*
     * Durable state:
     *
     * Sections initialized after this moment can
     * still see that OFFSTRIPE is already ready.
     */
    document.documentElement.dataset.offstripePageReady =
      'true';


    /*
     * Live event:
     *
     * Sections already initialized can react at
     * the exact moment the loader has disappeared.
     */
    document.dispatchEvent(
      new CustomEvent(
        'offstripe:page-ready'
      )
    );
  }
}


new OffstripeLoader();